import { useQuery, useMutation } from "@tanstack/react-query";
import * as yup from "yup";
import api from "@/lib/api-client";
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { Input, Checkbox, Listbox } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { FormProvider, useForm } from "react-hook-form";
import { Link } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { CheckIcon } from "@heroicons/react/24/outline";

const schema = yup
    .object({
        firstName: yup.string().required("Please enter your first name"),
        lastName: yup.string().required("Please enter your last name"),
        email: yup
            .string()
            .required("Please enter your email address")
            .email("Enter an email like name@example.com"),
        roleType: yup.string().required("Please select a role"),
        discoveryType: yup.string().required("Please select an option"),
    })
    .required();

const InterestForm = () => {
    return (
        <GoogleReCaptchaProvider reCaptchaKey="6Ldi_YEsAAAAAAPn753-698rqVf9ZkBlt7AFlmyE">
            <InterestFormContent />
        </GoogleReCaptchaProvider>
    );
};

const InterestFormContent = () => {
    const methods = useForm({
        resolver: yupResolver(schema),
    });

    // Get discovery types
    const fetchDiscoveryTypes = async () => {
        const res = await api.get("/DiscoveryTypes");

        return res.data;
    };

    const { data: discoveryTypes = [] } = useQuery({
        queryKey: ["discoveryTypes"],
        queryFn: fetchDiscoveryTypes,
        retry: 3,
        staleTime: 1000 * 60 * 5,
    });

    const { executeRecaptcha } = useGoogleReCaptcha();

    // Submit
    const registerInterest = async (data) => {
        if (!executeRecaptcha) return;
        const captchaToken = await executeRecaptcha("submit_form");

        const payload = {
            ...data,
            optedIn: data.optedIn ? 1 : 0,
        };

        await api.post("/InterestForm", payload, {
            headers: {
                "x-captcha-token": captchaToken,
            },
        });
    };

    const mutation = useMutation({
        mutationFn: registerInterest,
        retry: 3,
    });

    const onSubmit = (data) => {
        mutation.mutate(data);
    };

    if (mutation.isSuccess) {
        return (
            <div className="mx-auto prose mt-5 grid max-w-6xl grid-cols-12 gap-x-8 gap-y-6 px-3 pb-24 prose-darkgreen md:mt-10 md:px-10 lg:mt-20">
                <div className="col-span-full flex justify-center">
                    <div className="flex size-30 shrink-0 items-center justify-center rounded-full bg-emerald-950">
                        <CheckIcon className="size-15 shrink-0 stroke-3 text-darkgreen-500" />
                    </div>
                </div>
                <div className="col-span-full text-center">
                    <h1 className="mb-4 text-center text-6xl font-semibold tracking-wider">
                        Thank you for registering your interest in Welnido.
                    </h1>
                    <p className="lead mx-auto mb-0 max-w-3xl text-neutral-500">
                        We’re building a simpler, more connected way to access
                        healthcare - designed to support both patients and
                        clinicians. Your early interest genuinely means a lot to
                        us as we prepare for launch.
                    </p>
                    <p className="lead mx-auto mb-0 max-w-3xl text-neutral-500">
                        We’ll keep you updated via email with key milestones,
                        early access opportunities, and updates on when the
                        platform goes live.
                    </p>
                    <p className="lead mx-auto mb-0 max-w-3xl text-neutral-500">
                        We look forward to welcoming you to Welnido soon.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={methods.handleSubmit(onSubmit)}
                className="container mx-auto"
            >
                <div className="mx-auto prose grid max-w-6xl grid-cols-12 gap-x-4 gap-y-6 px-4 pb-24 prose-darkgreen md:pt-24">
                    <div className="col-span-full text-center">
                        <h1 className="mb-4 text-center text-6xl font-semibold tracking-wider">
                            Register your interest
                        </h1>
                        <p className="lead mx-auto mb-0 max-w-3xl text-neutral-500">
                            Our vision is to bring a sense of ease back into
                            healthcare. To replace scattered searches and
                            endless coordination with one clear, trusted place
                            to organise care. A space where professionals can
                            manage appointments simply, and patients can book
                            with confidence.
                        </p>
                    </div>
                    <div className="col-span-full lg:col-span-6">
                        <Input
                            name="firstName"
                            label="First name"
                            placeholder="First name"
                            hideLabel={true}
                        />
                    </div>
                    <div className="col-span-full lg:col-span-6">
                        <Input
                            name="lastName"
                            label="Last name"
                            placeholder="Last name"
                            hideLabel={true}
                        />
                    </div>
                    <div className="col-span-full lg:col-span-4">
                        <Input
                            name="email"
                            label="Email address"
                            placeholder="Email address"
                            hideLabel={true}
                        />
                    </div>
                    <div className="col-span-full lg:col-span-4">
                        <Listbox
                            name="roleType"
                            label="What is your role?"
                            placeholder="What is your role?"
                            hideLabel={true}
                        >
                            <option value="1">I am a patient</option>
                            <option value="2">I am a clinician</option>
                        </Listbox>
                    </div>
                    <div className="col-span-full lg:col-span-4">
                        <Listbox
                            name="discoveryType"
                            label="Where did you here about us?"
                            placeholder="Where did you here about us?"
                            hideLabel={true}
                        >
                            {discoveryTypes.map((d) => (
                                <option key={d.typeId} value={d.typeId}>
                                    {d.title}
                                </option>
                            ))}
                        </Listbox>
                    </div>
                    <div className="col-span-full text-center lg:col-span-8 lg:col-start-3">
                        <Checkbox
                            name="optedIn"
                            label={
                                <>
                                    Tick this box if you want to receive emails
                                    from us. You can unsubscribe at any time by
                                    clicking the unsubscribe link in those
                                    emails. You can find out more about how we
                                    store and use your data in our{" "}
                                    <Link
                                        to="/privacy"
                                        target="_blank"
                                        className="text-inherit"
                                    >
                                        Privacy Notice.
                                    </Link>
                                </>
                            }
                            labelPlacement="end"
                        />
                    </div>
                    <div className="col-start-2 col-end-12 mt-15 grid md:col-start-5 md:col-end-9">
                        <Button
                            type="submit"
                            id="submitInterestForm"
                            variant="primary"
                            disabled={mutation.isPending || !executeRecaptcha}
                            className="w-full"
                        >
                            {mutation.isPending ? "Submitting..." : "Submit"}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

export default InterestForm;
